import React, { useState, useMemo, useEffect } from 'react';
import { useMangaSource } from '../context/MangaSourceContext';
import { useAniList } from '../context/AniListContext';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { useUndoRedo } from '../context/UndoRedoContext';
import { useLinkSettings } from '../context/LinkSettingsContext';
import { useAlert } from '../context/ToastContext';
import { formatChapterProgress, getTotalChapters, getTotalVolumes, calculateMangaProgress, getMangaMediaStatusBadge } from '../utils/timeFormatter';
import { formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { updateMangaProgress, updateMangaVolumeProgress, updateAnimeStatus, updateMediaScore } from '../services/aniListService';
import { getSimpleCache } from '../services/cacheService';
import { LOCAL_STORAGE_KEYS } from '../config/constants';
import { CardGridSkeleton } from './common/SkeletonLoader';
import StatusButton from './StatusButton';
import EpisodeCounter from './EpisodeCounter';
import SiteLinksModal from './SiteLinksModal';
import ScoreInput from './ScoreInput';
import './QuickWins.css';
import './Dashboard.css';

const SORT_OPTIONS = [
    { id: 'remainingChapters', label: '⏳ Remaining Chapters' },
    { id: 'name', label: '📝 Name' },
    { id: 'averageScore', label: '⭐ Average Score' },
    { id: 'totalManga', label: '📚 Total Manga' },
    { id: 'totalChapters', label: '📖 Total Chapters' },
    { id: 'readChapters', label: '✅ Read Chapters' }
];

const GROUPS_PER_PAGE = 50;
const MANGA_QW_SORT_KEY = 'manga_quick_wins_sort_by';
const MANGA_QW_DIR_KEY = 'manga_quick_wins_sort_direction';
const MANGA_QW_CHAPTERS_KEY = 'manga_quick_wins_chapters';

const MangaQuickWins = () => {
    const { token } = useAniList();
    const { mangaGroups, mangaUserData, isLoading, refreshData, latestChapterMap } = useMangaSource();
    const { titlePreference } = useTitlePreference();
    const { addChange } = useUndoRedo();
    const { readLinks } = useLinkSettings();
    const { showError } = useAlert();

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showReadModal, setShowReadModal] = useState(false);
    const [selectedMangaForModal, setSelectedMangaForModal] = useState('');
    const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
    const [changingStatus, setChangingStatus] = useState(new Set());
    const [optimisticProgress, setOptimisticProgress] = useState(new Map());
    const [updatingProgress, setUpdatingProgress] = useState(new Set());
    const [optimisticVolumes, setOptimisticVolumes] = useState(new Map());
    const [updatingVolumes, setUpdatingVolumes] = useState(new Set());
    const [optimisticScores, setOptimisticScores] = useState(new Map());
    const [updatingScores, setUpdatingScores] = useState(new Set());
    const [quickWinChapters, setQuickWinChapters] = useState(() => {
        const stored = localStorage.getItem(MANGA_QW_CHAPTERS_KEY);
        return stored ? parseInt(stored, 10) : 20;
    });
    const [sortBy, setSortBy] = useState(() => localStorage.getItem(MANGA_QW_SORT_KEY) || 'remainingChapters');
    const [sortDirection, setSortDirection] = useState(() => localStorage.getItem(MANGA_QW_DIR_KEY) || 'asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [loadedPages, setLoadedPages] = useState(new Set([1]));

    // Persist sort & chapter threshold
    useEffect(() => { localStorage.setItem(MANGA_QW_SORT_KEY, sortBy); }, [sortBy]);
    useEffect(() => { localStorage.setItem(MANGA_QW_DIR_KEY, sortDirection); }, [sortDirection]);
    useEffect(() => { localStorage.setItem(MANGA_QW_CHAPTERS_KEY, String(quickWinChapters)); }, [quickWinChapters]);

    // Filter and sort groups by chapter threshold
    const quickWinGroups = useMemo(() => {
        if (!mangaGroups || mangaGroups.length === 0) return [];

        const groupsWithRemaining = mangaGroups.map(group => {
            const entries = group.anime || [];
            const nonDropped = entries.filter(e => e.status !== 'DROPPED');
            if (nonDropped.length === 0) return { ...group, remainingChapters: 0, hasNonDropped: false, hasKnownTotals: false };

            let totalChapters = 0;
            let readChapters = 0;
            let hasKnownTotals = false;

            nonDropped.forEach(entry => {
                const total = getTotalChapters(entry, latestChapterMap);
                const read = entry.progress || 0;
                if (total !== null && total > 0) {
                    // Only count entries where we know the total
                    totalChapters += total;
                    readChapters += read;
                    hasKnownTotals = true;
                }
            });

            return {
                ...group,
                remainingChapters: hasKnownTotals ? totalChapters - readChapters : Infinity,
                hasNonDropped: true,
                hasKnownTotals
            };
        });

        // Filter by chapter threshold, exclude groups with only dropped manga or unknown totals
        let filtered = groupsWithRemaining.filter(
            group => group.hasNonDropped && group.hasKnownTotals && group.remainingChapters > 0 && group.remainingChapters <= quickWinChapters
        );

        // Sort
        const dir = sortDirection === 'asc' ? 1 : -1;
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return dir * a.name.localeCompare(b.name);
                case 'averageScore':
                    return dir * ((b.stats?.averageScore || 0) - (a.stats?.averageScore || 0));
                case 'totalChapters':
                    return dir * ((b.stats?.totalChapters || 0) - (a.stats?.totalChapters || 0));
                case 'readChapters':
                    return dir * ((b.stats?.readChapters || 0) - (a.stats?.readChapters || 0));
                case 'totalManga':
                    return dir * ((b.stats?.mangaCount || b.stats?.animeCount || 0) - (a.stats?.mangaCount || a.stats?.animeCount || 0));
                case 'remainingChapters':
                default:
                    return dir * (a.remainingChapters - b.remainingChapters);
            }
        });

        return filtered;
    }, [mangaGroups, quickWinChapters, latestChapterMap, titlePreference, sortBy, sortDirection]);

    // Pagination
    const displayedGroups = useMemo(() => {
        let groupsToShow = [];
        Array.from(loadedPages).sort((a, b) => a - b).forEach(page => {
            const start = (page - 1) * GROUPS_PER_PAGE;
            groupsToShow.push(...quickWinGroups.slice(start, start + GROUPS_PER_PAGE));
        });
        return groupsToShow;
    }, [quickWinGroups, loadedPages]);

    const totalPages = Math.ceil(quickWinGroups.length / GROUPS_PER_PAGE);

    useEffect(() => {
        setCurrentPage(1);
        setLoadedPages(new Set([1]));
    }, [quickWinChapters, sortBy, sortDirection]);

    useEffect(() => {
        if (currentPage === 1 && totalPages > 1 && !loadedPages.has(2)) {
            const timer = setTimeout(() => {
                setLoadedPages(prev => new Set([...prev, 2]));
                setCurrentPage(2);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [currentPage, totalPages, loadedPages]);

    const loadNextPage = React.useCallback(() => {
        const next = currentPage + 1;
        if (next <= totalPages && !loadedPages.has(next)) {
            setLoadedPages(prev => new Set([...prev, next]));
            setCurrentPage(next);
        }
    }, [currentPage, totalPages, loadedPages]);

    const handleScroll = React.useCallback(() => {
        const scrollPos = window.innerHeight + window.scrollY;
        const triggerHeight = document.documentElement.scrollHeight - 1000;
        if (scrollPos >= triggerHeight && currentPage < totalPages && !loadedPages.has(currentPage + 1)) {
            loadNextPage();
        }
    }, [currentPage, totalPages, loadedPages, loadNextPage]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const handleGroupClick = (groupId) => {
        const group = mangaGroups.find(g => g.id === groupId);
        if (group) setSelectedGroup(group);
    };

    // Handle manga status change
    const handleStatusChange = async (mediaId, currentStatus, newStatus, mangaTitle) => {
        if (newStatus === currentStatus) return;
        const tkn = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }

        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        addChange({
            type: 'STATUS_CHANGE',
            mediaId,
            animeTitle: mangaTitle,
            oldStatus: currentStatus,
            newStatus,
            timestamp: Date.now()
        });

        try {
            await updateAnimeStatus(mediaId, newStatus, tkn);
            if (refreshData) refreshData(true);
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        } catch (error) {
            setOptimisticUpdates(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
            showError(`Failed to update status: ${error.message}`, 'Status Update Failed');
        }
    };

    // Handle score change
    const handleScoreChange = async (mediaId, newScoreRaw, mangaTitle) => {
        const tkn = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }
        setOptimisticScores(prev => new Map(prev).set(mediaId, newScoreRaw));
        setUpdatingScores(prev => new Set([...prev, mediaId]));
        try {
            await updateMediaScore(mediaId, newScoreRaw, tkn);
            if (refreshData) refreshData(true);
        } catch (error) {
            showError(`Failed to update score for ${mangaTitle}`, 'Score Update Failed');
            setOptimisticScores(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingScores(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle chapter progress change
    const handleProgressChange = async (mediaId, newProgress, totalChapters, mangaTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newProgress);
        if (totalChapters > 0) clamped = Math.min(clamped, totalChapters);

        const tkn = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }

        setOptimisticProgress(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingProgress(prev => new Set([...prev, mediaId]));

        try {
            await updateMangaProgress(mediaId, clamped, tkn);
            if (refreshData) refreshData(true);

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Reading)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${mangaTitle} — progress set to ${clamped}, switching to Reading`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', mangaTitle);
            }

            // Auto-complete: if all chapters read and manga is finished, mark as COMPLETED
            if (clamped > 0 && totalChapters > 0 && clamped === totalChapters && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${mangaTitle} — all ${totalChapters} chapters read`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', mangaTitle);
            }
        } catch (error) {
            showError(`Failed to update progress for ${mangaTitle}`, 'Update Failed');
            setOptimisticProgress(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingProgress(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle volume progress change
    const handleVolumeProgress = async (mediaId, newVolumes, totalVolumes, mangaTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newVolumes);
        if (totalVolumes > 0) clamped = Math.min(clamped, totalVolumes);

        const tkn = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
        if (!tkn) { showError('Authentication required.', 'Auth Required'); return; }

        setOptimisticVolumes(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingVolumes(prev => new Set([...prev, mediaId]));

        try {
            await updateMangaVolumeProgress(mediaId, clamped, tkn);
            if (refreshData) refreshData(true);

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Reading)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${mangaTitle} — volume progress set to ${clamped}, switching to Reading`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', mangaTitle);
            }

            // Auto-complete: if all volumes read and manga is finished, mark as COMPLETED
            if (clamped > 0 && totalVolumes > 0 && clamped === totalVolumes && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${mangaTitle} — all ${totalVolumes} volumes read`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', mangaTitle);
            }
        } catch (error) {
            showError(`Failed to update volume for ${mangaTitle}`, 'Update Failed');
            setOptimisticVolumes(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingVolumes(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    const handleChaptersChange = (e) => {
        const val = parseInt(e.target.value, 10);
        if (!isNaN(val) && val >= 1) setQuickWinChapters(val);
    };

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (selectedGroup) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [selectedGroup]);

    if (isLoading && (!mangaGroups || mangaGroups.length === 0)) {
        return <CardGridSkeleton title="⚡ Quick Wins" subtitle="Manga groups you can complete soon" />;
    }

    if (!mangaGroups || mangaGroups.length === 0) {
        return (
            <div className="quick-wins-container">
                <div className="empty-state">
                    <div className="empty-icon">⚡</div>
                    <h2>No Groups Yet</h2>
                    <p>Your manga groups will appear here once loaded.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="quick-wins-container">
            <div className="quick-wins-header">
                <div className="header-left">
                    <h1>⚡ Quick Wins</h1>
                    <p>Manga groups you can complete soon</p>
                </div>
                <div className="quick-wins-filter">
                    <label htmlFor="quick-win-chapters">Only</label>
                    <input
                        id="quick-win-chapters"
                        type="number"
                        min="1"
                        max="9999"
                        value={quickWinChapters}
                        onChange={handleChaptersChange}
                        className="hours-input"
                    />
                    <span className="hours-label">chapters</span>
                    <div className="sort-controls">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="sort-select"
                            title="Sort by"
                        >
                            {SORT_OPTIONS.map(option => (
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                            className="sort-direction-btn"
                            title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                        >
                            {sortDirection === 'asc' ? '↑' : '↓'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="quick-wins-count">
                {quickWinGroups.length > 0
                    ? `${quickWinGroups.length} groups can be completed within ${quickWinChapters} chapter${quickWinChapters !== 1 ? 's' : ''}`
                    : `No groups can be completed within ${quickWinChapters} chapter${quickWinChapters !== 1 ? 's' : ''}`
                }
            </div>

            <div className="quick-wins-list">
                {quickWinGroups.length === 0 ? (
                    <div className="empty-state" style={{ textAlign: 'center', width: '100%' }}>
                        <p>No quick wins found. Try increasing the chapter threshold.</p>
                    </div>
                ) : (
                    displayedGroups.map((group, index) => {
                        const oldestManga = group.anime[0];
                        const coverImage = oldestManga?.media?.coverImage?.extraLarge || oldestManga?.media?.coverImage?.large || oldestManga?.media?.coverImage?.medium;
                        const groupName = getDisplayTitle(oldestManga?.media, titlePreference);
                        const hasReleasing = group.anime.some(a => a.media?.status === 'RELEASING');

                        return (
                            <div
                                key={group.id}
                                className="quick-win-card"
                                onClick={() => handleGroupClick(group.id)}
                                role="button"
                                tabIndex={0}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') handleGroupClick(group.id);
                                }}
                            >
                                <div className="quick-win-rank">#{index + 1}</div>

                                {coverImage && (
                                    <img
                                        src={coverImage}
                                        alt={groupName}
                                        className="quick-win-cover"
                                    />
                                )}

                                <div className="quick-win-info">
                                    <h3 className="quick-win-name">
                                        <a
                                            href={`https://anilist.co/manga/${oldestManga?.media?.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {groupName}
                                        </a>
                                    </h3>
                                    {hasReleasing && <span className="group-releasing-tag">📡 Publishing</span>}

                                    <div className="quick-win-stats">
                                        <span className="stat-anime-count">
                                            📚 {group.stats?.mangaCount || group.stats?.animeCount || 0} manga
                                        </span>
                                        <span>•</span>
                                        <span className="stat-episodes">
                                            📖 {formatChapterProgress(group.stats?.readChapters || 0, group.stats?.totalChapters || 0)} ch
                                        </span>
                                        {(group.stats?.averageScore || 0) > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-score">
                                                    ⭐ {(group.stats.averageScore / 10).toFixed(1)}
                                                </span>
                                            </>
                                        )}
                                    </div>

                                    <div className="quick-win-time-stats">
                                        <div className="time-stat remaining primary">
                                            <span className="time-label">⏳ To Complete</span>
                                            <span className="time-value">
                                                {group.remainingChapters} ch
                                                {(group.stats?.remainingVolumes || 0) > 0 && ` / ${group.stats.remainingVolumes} vol`}
                                            </span>
                                        </div>
                                        {(group.stats?.readChapters || 0) > 0 && (
                                            <div className="time-stat watched">
                                                <span className="time-label">✅ Read</span>
                                                <span className="time-value">
                                                    {group.stats.readChapters} ch
                                                    {(group.stats?.readVolumes || 0) > 0 && ` / ${group.stats.readVolumes} vol`}
                                                </span>
                                            </div>
                                        )}
                                        <div className="time-stat total">
                                            <span className="time-label">📖 Total</span>
                                            <span className="time-value">
                                                {group.stats?.totalChapters || '?'} ch
                                                {(group.stats?.totalVolumes || 0) > 0 && ` / ${group.stats.totalVolumes} vol`}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Group Card Modal Overlay */}
            {selectedGroup && (
                <div className="group-modal-overlay" onClick={() => setSelectedGroup(null)}>
                    <div className="group-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setSelectedGroup(null)}>×</button>

                        <div className="group-card-modal">
                            <div className="modal-group-header">
                                {selectedGroup.anime[0]?.media?.coverImage?.large && (
                                    <img
                                        src={selectedGroup.anime[0].media.coverImage.large}
                                        alt={getDisplayTitle(selectedGroup.anime[0]?.media, titlePreference)}
                                        className="modal-group-cover"
                                    />
                                )}
                                <div className="modal-group-info">
                                    <h2 className="modal-group-name">
                                        📚 {getDisplayTitle(selectedGroup.anime[0]?.media, titlePreference)}
                                    </h2>
                                    <div className="modal-group-stats">
                                        <span className="stat-anime-count">{selectedGroup.stats?.mangaCount || selectedGroup.stats?.animeCount || 0} manga</span>
                                        <span>•</span>
                                        <span className="stat-episodes">
                                            📖 {formatChapterProgress(selectedGroup.stats?.readChapters || 0, selectedGroup.stats?.totalChapters || 0)} ch
                                        </span>
                                        {(selectedGroup.stats?.remainingChapters || 0) > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-remaining-time">⏳ {selectedGroup.stats.remainingChapters} ch remaining</span>
                                            </>
                                        )}
                                        {(selectedGroup.stats?.averageScore || 0) > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-score">⭐ {(selectedGroup.stats.averageScore / 10).toFixed(1)}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-anime-list">
                                {selectedGroup.anime.map((entry) => {
                                    const title = getDisplayTitle(entry.media, titlePreference);
                                    const releaseDate = formatReleaseDate(entry.media.startDate);
                                    const totalChapters = getTotalChapters(entry, latestChapterMap);
                                    const readChapters = entry.progress || 0;
                                    const displayStatus = optimisticUpdates.get(entry.media.id) || entry.status;
                                    const readChaptersDisplay = optimisticProgress.has(entry.media.id) ? optimisticProgress.get(entry.media.id) : readChapters;
                                    const mangaProgress = calculateMangaProgress(entry, latestChapterMap);

                                    return (
                                        <div key={entry.id} className="modal-anime-item">
                                            <img
                                                src={entry.media.coverImage?.extraLarge || entry.media.coverImage?.large || entry.media.coverImage?.medium}
                                                alt={title}
                                                className="anime-cover"
                                            />
                                            <div className="anime-details">
                                                <div className="anime-title-row">
                                                    <h4 className="anime-title">
                                                        <a
                                                            href={`https://anilist.co/manga/${entry.media.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {title}
                                                        </a>
                                                    </h4>
                                                    <div className="anime-action-buttons">
                                                        <ScoreInput
                                                            mediaId={entry.media.id}
                                                            currentScore={optimisticScores.has(entry.media.id) ? optimisticScores.get(entry.media.id) : (entry.score || 0)}
                                                            isUpdating={updatingScores.has(entry.media.id)}
                                                            onChange={(newScore) => handleScoreChange(entry.media.id, newScore, title)}
                                                        />
                                                        <EpisodeCounter
                                                            mediaId={entry.media.id}
                                                            currentProgress={readChaptersDisplay}
                                                            totalEpisodes={totalChapters}
                                                            isUpdating={updatingProgress.has(entry.media.id)}
                                                            onChange={(newVal) => handleProgressChange(entry.media.id, newVal, totalChapters, title, entry.media.status, displayStatus)}
                                                            label="chapter"
                                                        />
                                                        {(() => {
                                                            const totalVolumes = getTotalVolumes(entry);
                                                            const readVolumes = entry.progressVolumes || 0;
                                                            return totalVolumes > 0 ? (
                                                                <EpisodeCounter
                                                                    mediaId={`vol-${entry.media.id}`}
                                                                    currentProgress={optimisticVolumes.has(entry.media.id) ? optimisticVolumes.get(entry.media.id) : readVolumes}
                                                                    totalEpisodes={totalVolumes}
                                                                    isUpdating={updatingVolumes.has(entry.media.id)}
                                                                    onChange={(newVal) => handleVolumeProgress(entry.media.id, newVal, totalVolumes, title, entry.media.status, displayStatus)}
                                                                    label="volume"
                                                                />
                                                            ) : null;
                                                        })()}
                                                        <StatusButton
                                                            currentStatus={displayStatus}
                                                            onChange={(newStatus) => handleStatusChange(entry.media.id, displayStatus, newStatus, title)}
                                                            disabled={changingStatus.has(entry.media.id)}
                                                            isLoading={changingStatus.has(entry.media.id)}
                                                            type="manga"
                                                        />
                                                        <button
                                                            className="action-btn watch-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedMangaForModal(title);
                                                                setShowReadModal(true);
                                                            }}
                                                            title="Read"
                                                        >
                                                            📖
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="anime-meta">
                                                    <span>{releaseDate}</span>
                                                    <span>{entry.media.format}</span>
                                                    <span>📖 {readChapters}/{totalChapters || '?'} ch</span>
                                                    {mangaProgress.remainingChapters > 0 && (
                                                        <span>⏳ {mangaProgress.remainingChapters} ch{mangaProgress.remainingVolumes > 0 ? ` / ${mangaProgress.remainingVolumes} vol` : ''}</span>
                                                    )}
                                                    {entry.score > 0 && <span>⭐ {(entry.score / 10).toFixed(1)}</span>}
                                                    {entry.media.averageScore > 0 && <span>📊 {(entry.media.averageScore / 10).toFixed(1)}</span>}
                                                    {(() => { const badge = getMangaMediaStatusBadge(entry.media?.status); return <span className={`media-status-badge ${badge.className}`}>{badge.emoji} {badge.label}</span>; })()}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Site Links Modal */}
            <SiteLinksModal
                isOpen={showReadModal}
                onClose={() => {
                    setShowReadModal(false);
                    setSelectedMangaForModal('');
                }}
                links={readLinks}
                animeName={selectedMangaForModal || ''}
                type="read"
            />
        </div>
    );
};

export default MangaQuickWins;
